/**
 * Vehicle Inspection Status — Single Source of Truth for DISPLAY and ENFORCEMENT logic
 *
 * - `shouldRequireVehicleInspection` (in workflowEngine.ts) is the low-level rules
 *   checker used by the state machine. It checks inspection rules against inspection
 *   data but does NOT check vehicle selection.
 * - `isInspectionEnforcementRequired` (below) is the high-level UI enforcement check
 *   that wraps `shouldRequireVehicleInspection` with additional gates: vehicle
 *   selection and workflow state. Use this in all UI components that need to
 *   determine whether to block actions.
 * - `getInspectionDisplayStatus` provides user-friendly display strings.
 *
 * UI surfaces using these functions:
 *   - MyVehicleModal        (driver-facing vehicle overview)
 *   - VehicleOverviewTab    (fleet management admin view)
 *   - VehicleInspectionTab  (fleet management inspection tab)
 *   - BeginActionModal      (via vehicleGatePreflightCheck + isInspectionEnforcementRequired)
 *   - SmartTaskShell        (renderPanelSmartAction, launchTravelForWorkItem, handleSmartAction)
 */

import { shouldRequireVehicleInspection } from '../engine/workflowEngine';
import type { OperationalRules } from '../types';

export interface InspectionRules {
  isNonDriver?: boolean;
  requireDailyPreTripInspection?: boolean;
  requireTakeOnInspection?: boolean;
  requireHandoverInspection?: boolean;
  requireAdminPresentForInspection?: boolean;
  requireRandomInspection?: boolean;
  randomInspectionLeniencyDays?: number;
  scheduledInspectionInterval?: 'none' | 'day' | 'week' | 'fortnight' | 'month' | 'quarter' | string;
  allowInspectionRemindLater?: boolean;
}

export interface InspectionData {
  hasInspectionToday?: boolean;
  hasInspectionForVehicle?: boolean;
  lastInspectionDate?: string | null;
  /** True when a random inspection task is overdue (leniency expired) */
  randomInspectionOverdue?: boolean;
  /** True when a scheduled inspection task is past its due date */
  scheduledInspectionOverdue?: boolean;
  /** True when an admin has authorized a single-use inspection skip */
  inspectionOverridden?: boolean;
}

/**
 * True when any vehicle-inspection rule is configured — the rule toggles
 * themselves are the only switch for the inspection capability (there is no
 * separate master enable flag).
 */
export function hasAnyInspectionRules(rules: InspectionRules | null | undefined): boolean {
  if (!rules) return false;
  return !!(
    rules.requireDailyPreTripInspection
    || rules.requireTakeOnInspection
    || rules.requireHandoverInspection
    || rules.requireRandomInspection
    || (rules.scheduledInspectionInterval != null && rules.scheduledInspectionInterval !== 'none')
  );
}

export type InspectionTone = 'green' | 'amber' | 'red' | 'slate' | 'blue';

export interface InspectionStatusInfo {
  /** Short label for badges / stat cards */
  label: string;
  /** Longer descriptive text explaining the status */
  detail: string;
  /** Visual tone for badges / cards */
  tone: InspectionTone;
  /** True when an inspection is currently required or overdue */
  isRequired: boolean;
  /** True when no inspection rules are configured (inspections never needed) */
  isNotRequired: boolean;
  /** Suggested action to resolve, or 'none' when satisfied */
  action: 'complete_inspection' | 'none';
}

/**
 * Compute a user-friendly inspection status for display.
 *
 * Scenarios:
 *  1. Non-driver → "Not required"
 *  2. No rules enabled → "Not required"
 *  3. Daily pre-trip + done today → "Completed today" / "Next due tomorrow morning"
 *  4. Daily pre-trip + not done today → "Overdue" / "Complete before starting work"
 *  5. On vehicle change + has inspection → "On file" / "Next on vehicle handover"
 *  6. On vehicle change + no inspection → "Take-on required" / "Complete before proceeding"
 */
export function getInspectionDisplayStatus(
  rules: InspectionRules,
  data: InspectionData,
): InspectionStatusInfo {
  // 1. Non-driver — inspections never apply
  if (rules.isNonDriver) {
    return {
      label: 'Not required',
      detail: 'Non-driver mode — vehicle inspections are not required.',
      tone: 'slate',
      isRequired: false,
      isNotRequired: true,
      action: 'none',
    };
  }

  // Admin override active — inspection requirement waived for this cycle
  if (data.inspectionOverridden) {
    return {
      label: 'Overridden',
      detail: 'Inspection requirement overridden by admin — you may proceed without completing it.',
      tone: 'blue',
      isRequired: false,
      isNotRequired: false,
      action: 'none',
    };
  }

  const dailyRequired = !!rules.requireDailyPreTripInspection;
  const onChangeRequired = !!rules.requireTakeOnInspection || !!rules.requireHandoverInspection;

  // 1c. Overdue inspection task — random or scheduled tasks gate even when no
  // daily/take-on rule is configured (the pending task is the trigger).
  if (data.randomInspectionOverdue || data.scheduledInspectionOverdue) {
    return {
      label: 'Due now',
      detail: data.scheduledInspectionOverdue
        ? 'A scheduled vehicle inspection is overdue. Complete it before starting travel.'
        : 'A random vehicle inspection is overdue. Complete it before starting travel.',
      tone: 'red',
      isRequired: true,
      isNotRequired: false,
      action: 'complete_inspection',
    };
  }

  // 2. No inspection rules enabled
  if (!dailyRequired && !onChangeRequired) {
    return {
      label: 'Not required',
      detail: 'No inspection requirements configured for this role.',
      tone: 'slate',
      isRequired: false,
      isNotRequired: true,
      action: 'none',
    };
  }

  const hasToday = !!data.hasInspectionToday;
  const hasForVehicle = !!data.hasInspectionForVehicle;

  // 3. Daily pre-trip inspection — completed today
  if (dailyRequired && hasToday) {
    return {
      label: 'Completed today',
      detail: 'Daily pre-trip inspection completed. Next inspection due tomorrow morning.',
      tone: 'green',
      isRequired: false,
      isNotRequired: false,
      action: 'none',
    };
  }

  // 4. Daily pre-trip inspection — not done today
  if (dailyRequired && !hasToday) {
    return {
      label: 'Due now',
      detail: hasForVehicle
        ? 'Daily pre-trip inspection is overdue. Complete it before starting any workflow.'
        : 'Daily pre-trip inspection is required before starting any workflow.',
      tone: 'red',
      isRequired: true,
      isNotRequired: false,
      action: 'complete_inspection',
    };
  }

  // 5. On vehicle change — has a previous inspection for this vehicle
  if (onChangeRequired && !dailyRequired && hasForVehicle) {
    return {
      label: 'On file',
      detail: 'Vehicle take-on inspection is on file. Next inspection due on vehicle handover.',
      tone: 'green',
      isRequired: false,
      isNotRequired: false,
      action: 'none',
    };
  }

  // 6. On vehicle change — no inspection yet for this vehicle
  if (onChangeRequired && !dailyRequired && !hasForVehicle) {
    return {
      label: 'Take-on required',
      detail: 'Complete the vehicle take-on inspection before proceeding with workflows.',
      tone: 'amber',
      isRequired: true,
      isNotRequired: false,
      action: 'complete_inspection',
    };
  }

  // Fallback — should not reach here if rules are consistent
  return {
    label: 'Unknown',
    detail: 'Inspection status could not be determined.',
    tone: 'slate',
    isRequired: false,
    isNotRequired: false,
    action: 'none',
  };
}

/**
 * High-level UI enforcement check — the single function all UI components should call
 * to determine whether inspection requirements block workflow actions.
 *
 * Wraps `shouldRequireVehicleInspection` (low-level rules check) with additional
 * UI-level gates: feature flag, vehicle selection, and completing state.
 */
export function isInspectionEnforcementRequired(
  rules: OperationalRules,
  context: {
    currentVehicleId: string | null | undefined;
    hasInspectionToday: boolean;
    hasInspectionForVehicle: boolean;
    currentState?: string;
    inspectionOverridden?: boolean;
    /** True when a random inspection task is overdue (leniency expired) */
    randomInspectionOverdue?: boolean;
    /** True when a scheduled inspection task is past its due date */
    scheduledInspectionOverdue?: boolean;
  },
): boolean {
  // Non-drivers are gated by the vehicle's inspection status too: a shared
  // vehicle must not travel uninspected regardless of which assignee tapped
  // Start Travel. The UI intercepts their tap with a driver-only notice —
  // they never enter inspection_pending or perform the inspection.
  if (!context.currentVehicleId) return false;
  if (context.currentState === 'completing') return false;
  if (context.inspectionOverridden) return false;

  // Rule 5: Random inspection — gate travel only after leniency expires
  if (rules.requireRandomInspection && context.randomInspectionOverdue) {
    return true;
  }

  // Scheduled inspection — an overdue scheduled task gates travel regardless
  // of the interval setting; the task itself is the trigger.
  if (context.scheduledInspectionOverdue) {
    return true;
  }

  return shouldRequireVehicleInspection(rules, {
    hasInspectionToday: context.hasInspectionToday,
    hasInspectionForVehicle: context.hasInspectionForVehicle,
    nonDriverCollectiveGate: true,
  });
}

/**
 * Get a short gate message for use in pre-flight checks and banners.
 * This replaces the hard-coded "Complete the vehicle take-on inspection"
 * message with context-aware text.
 */
export function getInspectionGateMessage(
  rules: InspectionRules,
  context?: { randomInspectionOverdue?: boolean; scheduledInspectionOverdue?: boolean },
): string {
  if (rules.isNonDriver) {
    return 'A vehicle inspection is required before travel can begin. Only the assigned driver can complete it.';
  }

  if (context?.scheduledInspectionOverdue) {
    return 'A scheduled vehicle inspection is overdue. Complete the inspection before starting travel.';
  }

  if (context?.randomInspectionOverdue || rules.requireRandomInspection) {
    return 'A random vehicle inspection has been triggered and the leniency period has expired. Complete the inspection before starting travel.';
  }

  if (rules.requireDailyPreTripInspection) {
    return 'Daily pre-trip inspection is required. Complete the vehicle inspection before starting any workflow.';
  }

  if (rules.requireHandoverInspection) {
    return 'Vehicle handover inspection is required. Complete the inspection before proceeding with workflows.';
  }

  if (rules.requireTakeOnInspection) {
    return 'Vehicle take-on inspection is required. Complete the inspection before proceeding with workflows.';
  }

  return '';
}

/**
 * Derive inspection status from a team member's `current_operations` (from the
 * profile API). This is the admin-facing equivalent of `getInspectionDisplayStatus`
 * — it uses the server-side `workflow_state` field (the state machine's determination)
 * rather than fetched inspection entries.
 *
 * Used by TeamMemberSmartTaskTab, TeamMemberOverviewTab, and
 * TeamMemberVehicleInspectionsTab so they all show the same inspection status.
 */
export function getTeamMemberInspectionStatus(
  currentOperations: {
    workflow_state?: string | null;
    inspection_overridden?: boolean | null;
    inspection_required?: boolean | null;
    is_non_driver?: boolean | null;
    current_vehicle_id?: string | null;
    current_vehicle_registration?: string | null;
  } | null | undefined,
): InspectionStatusInfo {
  if (!currentOperations) {
    return {
      label: 'Unknown',
      detail: 'No workflow data available.',
      tone: 'slate',
      isRequired: false,
      isNotRequired: false,
      action: 'none',
    };
  }

  const state = currentOperations.workflow_state;
  const hasVehicle = !!currentOperations.current_vehicle_id;

  // Non-drivers never own vehicle inspections — the driver of the vehicle does.
  if (currentOperations.is_non_driver === true) {
    return {
      label: 'Non-driver',
      detail: 'This member is a non-driver — vehicle inspections are handled by the driver.',
      tone: 'slate',
      isRequired: false,
      isNotRequired: true,
      action: 'none',
    };
  }
  const overridden = !!currentOperations.inspection_overridden;
  const reg = currentOperations.current_vehicle_registration;

  // No vehicle selected — inspection not applicable yet
  if (!hasVehicle && state !== 'inspection_pending') {
    return {
      label: 'No vehicle',
      detail: 'No vehicle assigned — inspection not required yet.',
      tone: 'slate',
      isRequired: false,
      isNotRequired: true,
      action: 'none',
    };
  }

  // Admin override active — inspection requirement waived
  if (overridden) {
    return {
      label: 'Overridden',
      detail: 'Inspection requirement overridden by admin.',
      tone: 'blue',
      isRequired: false,
      isNotRequired: false,
      action: 'none',
    };
  }

  // Inspection is due — either the state machine is in inspection_pending
  // (set at vehicle selection/assignment) or the server-computed
  // inspection_required flag says the driver's daily/take-on rule is
  // unsatisfied (mirrors the driver-side enforcement gate, which fires
  // regardless of current_state — e.g. daily pre-trip not yet done today).
  if (state === 'inspection_pending' || currentOperations.inspection_required === true) {
    return {
      label: 'Inspection due',
      detail: reg
        ? `Vehicle inspection required for ${reg} before starting travel.`
        : 'Vehicle inspection required before starting travel.',
      tone: 'red',
      isRequired: true,
      isNotRequired: false,
      action: 'complete_inspection',
    };
  }

  // Vehicle selected, not in inspection_pending — inspection satisfied or not required
  if (hasVehicle) {
    return {
      label: 'Up to date',
      detail: reg
        ? `Inspection completed for ${reg}.`
        : 'Inspection completed.',
      tone: 'green',
      isRequired: false,
      isNotRequired: false,
      action: 'none',
    };
  }

  return {
    label: 'Unknown',
    detail: 'Inspection status could not be determined.',
    tone: 'slate',
    isRequired: false,
    isNotRequired: false,
    action: 'none',
  };
}